import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { head } from 'lodash/fp';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { Icon } from 'ui';
import { scormUploadToFilestack } from 'misc/scormUpload';
import FileUpload from './FileUpload';
import UploadStatus from './UploadStatus';

const initialMessage = t('questions.types.elearning.file_upload.upload_file');
const fileProcessingMessage = t(
  'questions.types.elearning.file_upload.package_processing',
);
const fileUploadedMessage = t(
  'questions.types.elearning.file_upload.package_processed',
);

const ElearningFileUploader = ({
  setFileUploadError,
  setUploadSuccessful,
  scormId,
  canUploadFile,
  filename,
}) => {
  const [unmounted, setUnmounted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(!!filename);
  const [uploadFileName, setUploadFileName] = useState(filename);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [canReplaceFile, setCanReplaceFile] = useState(canUploadFile);
  const [processingProgress, setProcessingProgress] = useState(1);
  const [message, setMessage] = useState(
    filename ? fileUploadedMessage : initialMessage,
  );
  const [warning, setWarning] = useState(null);
  const fileUploadRef = useRef();

  useEffect(() => {
    return () => {
      setUnmounted(true);
    };
  }, []);

  const isFileValid = file => {
    return /\.zip/i.test(file.name);
  };

  const handleUploadFail = response => {
    if (!unmounted) {
      const uploadErrorMessage =
        response.message ||
        response.responseText ||
        t('questions.types.elearning.file_upload.error.upload_failed');

      setFileUploadError(uploadErrorMessage);
      setUploading(false);
      setIsFileUploaded(false);
      setUploadProgress(0);
      setProcessingProgress(1);
      setMessage(initialMessage);
      setCanReplaceFile(canUploadFile);
    }
  };

  const handleScormSuccess = response => {
    if (!unmounted && response.successful) {
      setUploadSuccessful(response.version);
      setUploading(false);
      setIsFileUploaded(true);
      setMessage(fileUploadedMessage);
      setWarning(response.warning);
      setCanReplaceFile(false);
    } else {
      handleUploadFail(response);
    }
  };

  const statusCheck = (token, importJobId) => {
    request
      .get(routes.SCORM_STATUS(scormId), {
        token,
        importJobId,
        updating: true,
      })
      .then(response => {
        switch (response.status) {
          case 'COMPLETE':
            setProcessingProgress(50);
            setTimeout(() => handleScormSuccess(response), 2000);
            break;
          case 'RUNNING':
            setTimeout(() => statusCheck(token, importJobId), 1000);
            setProcessingProgress(prevState => Math.min(40, prevState + 1));
            setMessage(fileProcessingMessage);
            break;
          default:
            handleUploadFail(response);
        }
      })
      .fail(() => {
        const failureMessage = {
          error: t(
            'questions.types.elearning.file_upload.error.connection_lost',
          ),
        };
        handleUploadFail(failureMessage);
      });
  };

  const trackUploadProgress = newProgress => {
    if (newProgress.totalPercent) {
      setUploadProgress(newProgress.totalPercent / 2);
    }
  };

  const startScormUploadJob = response => {
    const filestackHandle = response.handle;
    setMessage(fileProcessingMessage);
    request
      .putJSON(routes.SCORM_START(scormId), {
        filestack_handle: filestackHandle,
        updating: true,
      })
      .then(payload => {
        if (!unmounted && (payload.token || payload.importJobId)) {
          statusCheck(payload.token, payload.importJobId);
        } else {
          handleUploadFail();
        }
      })
      .fail(handleUploadFail);
  };

  const uploadScorm = file => {
    scormUploadToFilestack(file, trackUploadProgress).then(startScormUploadJob);
  };

  const fileUpload = (e, files) => {
    const originalFile = head(files || e.target.files);
    const sanatizedFilename = originalFile.name.replace(/[^\w.]/gi, '');
    const file = new File([originalFile], sanatizedFilename, {
      type: originalFile.type,
    });

    setFileUploadError('');
    if (isFileValid(file)) {
      setIsFileUploaded(false);
      setUploading(true);
      setUploadFileName(file.name);
      setMessage(t('questions.types.elearning.file_upload.package_uploading'));
      uploadScorm(file);
    } else {
      setFileUploadError(
        t('questions.types.elearning.file_upload.error.file_type'),
      );
      if (e) e.target.value = '';
    }
  };

  const uploaderClasses = classNames('ElearningFileUpload', {
    'ElearningFileUpload--uploading': uploading,
    'ElearningFileUpload--uploadSuccess': isFileUploaded,
  });
  const showUploadStatus = uploading || isFileUploaded;

  return (
    <div>
      {warning && (
        <div className="ErrorContainer ErrorContainer--brandYellow Animated--fadeIn mb-8 d-f ai-c">
          <Icon name="warning_sign" className="mr-8" />
          <span className="text-wrap">{warning}</span>
        </div>
      )}
      <div className={uploaderClasses}>
        {showUploadStatus ? (
          <UploadStatus
            uploading={uploading}
            isFileUploaded={isFileUploaded}
            message={message}
            uploadFileName={uploadFileName}
            canReplaceFile={canReplaceFile}
            fileUploadRef={fileUploadRef}
            uploadProgress={uploadProgress}
            processingProgress={processingProgress}
          />
        ) : (
          <FileUpload
            fileUpload={fileUpload}
            fileUploadRef={fileUploadRef}
            message={message}
          />
        )}
      </div>
      <input
        className="ElearningFileUpload-upload-btn"
        ref={fileUploadRef}
        type="file"
        onChange={fileUpload}
        disabled={uploading}
        tabIndex="-1"
      />
    </div>
  );
};

export default ElearningFileUploader;
